'use client';

import { useState } from 'react';
import { Search } from 'lucide-react';
import { useIsDesktop } from '@/shared/hooks/use-is-desktop';
import { useIsScrolledFromTop } from '@/shared/hooks/use-is-scrolled-from-top';
import { cn } from '@ecom/helper-react-class-combine';
import { CartLink } from '@/features/cart/shared/components/cart-link';
import { useIsWTGamesApp } from '@/features/games-app-bridge/shared/hooks/use-is-wt-games-app';
import { GamesSearchInput } from '@/features/games/shared/components/games-searchbar/games-search-input';
import { MenuButton } from '@/features/layout/components/menu-button';
import { ResponsiveMenu } from '@/features/layout/components/responsive-menu';
import { UserActionLinks } from '@/features/layout/components/user-action-links';
import { WTLogoHomeLink } from '@/features/layout/components/wt-logo-home-link';
import { DesktopNavMenu } from '@/features/layout/components/desktop-nav-menu';
import { TokenMenuItem } from '@/features/membership/shared/components/token-menu-item/token-menu-item';
import { usePathname } from '@/i18n/routing';

const HEADERLESS_PATTERNS: ReadonlyArray<RegExp> = [/^\/get-started\/[^/]+$/];

export const Header = () => {
  const pathname = usePathname();
  if (pathname && HEADERLESS_PATTERNS.some((pattern) => pattern.test(pathname))) {
    return null;
  }
  return <HeaderInner />;
};

const HeaderInner = () => {
  const [isNavigationMenuOpen, setIsNavigationMenuOpen] = useState(false);
  const [isResponsiveSearchInputOpen, setIsResponsiveSearchInputOpen] = useState(false);
  const [isResponsiveMenuOpen, setIsResponsiveMenuOpen] = useState(false);

  const isDesktop = useIsDesktop();

  const isWTGamesApp = useIsWTGamesApp();

  const { isScrolledFromTop: isScrolledRaw } = useIsScrolledFromTop(() => {
    if (isDesktop) {
      setIsNavigationMenuOpen(false);
    } else {
      setIsResponsiveSearchInputOpen(false);
    }
  });

  const isScrolledFromTop = isWTGamesApp ? false : isScrolledRaw;

  return (
    <>
      <header
        className={cn(
          'bg-background sticky top-0 z-40 transition-[height] duration-300 ease-in-out max-lg:mb-2',
          {
            'border-primary h-20 border-b max-lg:h-12': isScrolledFromTop,
            'h-36 max-lg:h-24': !isScrolledFromTop,
            'h-36 border-none':
              (isNavigationMenuOpen && isDesktop) || (isResponsiveSearchInputOpen && !isDesktop),
          },
        )}
      >
        <div className='container mx-auto grid max-w-7xl grid-cols-[1fr_auto] grid-rows-[auto_auto] items-center md:max-w-screen-2xl lg:h-20 lg:grid-cols-[auto_1fr_auto] lg:grid-rows-1 lg:px-4'>
          <div className='col-span-1 flex items-center max-lg:h-12 max-lg:pl-4'>
            <MenuButton
              isVisible={(isScrolledFromTop && !isNavigationMenuOpen) || !isDesktop}
              onClick={() => {
                if (isDesktop) {
                  setIsNavigationMenuOpen(true);
                } else {
                  setIsResponsiveMenuOpen(true);
                }
              }}
            />
            <WTLogoHomeLink className='max-lg:m-auto' />
          </div>
          <div className='relative col-start-2 row-start-1 flex items-center justify-end max-lg:pr-4 lg:col-start-3 lg:px-4'>
            <UserActionLinks className='max-lg:hidden' />
            <div
              className={cn(
                'absolute right-20 cursor-pointer transition-all duration-300 ease-in-out lg:hidden',
                {
                  'top-1/2 -translate-y-1/2 opacity-100':
                    isScrolledFromTop && !isResponsiveSearchInputOpen,
                  'invisible top-0 -translate-y-full opacity-0':
                    !isScrolledFromTop || isResponsiveSearchInputOpen,
                },
              )}
              onClick={() => setIsResponsiveSearchInputOpen(true)}
            >
              <Search size={22} />
            </div>
            <CartLink />
            <TokenMenuItem />
          </div>
          <div
            className={cn(
              'col-span-full row-start-2 w-full lg:col-span-1 lg:col-start-2 lg:row-start-1',
              'max-lg:transition-all max-lg:duration-300 max-lg:ease-in-out',
              {
                'max-lg:max-h-12 max-lg:scale-y-100':
                  (!isScrolledFromTop && !isDesktop) || isResponsiveSearchInputOpen,
                'max-lg:max-h-0 max-lg:origin-top max-lg:scale-y-0':
                  isScrolledFromTop && !isResponsiveSearchInputOpen,
              },
            )}
            aria-hidden={isScrolledFromTop && !isDesktop}
          >
            <GamesSearchInput />
          </div>
        </div>

        <div
          className={cn('transition-all duration-300 ease-in-out max-lg:hidden', {
            'max-h-14 scale-y-100': (!isScrolledFromTop && isDesktop) || isNavigationMenuOpen,
            'max-h-0 origin-top scale-y-0': isScrolledFromTop && !isNavigationMenuOpen,
          })}
        >
          <DesktopNavMenu />
        </div>
        {!isDesktop && (
          <ResponsiveMenu
            isOpen={!isDesktop && isResponsiveMenuOpen}
            onClose={() => setIsResponsiveMenuOpen(false)}
          />
        )}
      </header>
    </>
  );
};
